// crewbit-admin/src/hooks/useFinancialSummary.ts
//
// Mirrors crewbit-mobile/hooks/useFinancialSummary.ts.
// Hook contract: docs/references/MONEY_MODULE_WIRING_v1.md §3.9.
// RPC envelope: financial_summary post-Session-116 migrations 255 / 256 / 257.
//
// Refresh policy:
//   - Initial mount → fetch.
//   - Page Visibility API → throttled to 30s; manual refetch() always fires.
//   - Stale-while-revalidate: keep stale data visible while refetching.
//   - Cache-invalidate: re-fetch on the 'crewbit:active-business-changed'
//     window event and the 'crewbit:money-mutation' window event (mutating
//     Money hooks dispatch the latter on success).
//
// The three-runtime duplication (mobile RN, admin web, web JSX) is intentional
// per WIRING §1.1.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { callMoneyRpc } from '../lib/moneyRpc';
import type { MoneyError } from '../lib/errorMap';
import {
  useActiveBusinessStore,
  ACTIVE_BUSINESS_CHANGED_EVENT,
} from './useActiveBusiness';

export type FinancialSummaryPeriod = '7d' | '30d' | 'mtd' | 'qtd' | 'ytd' | 'custom';

export const MONEY_MUTATION_EVENT = 'crewbit:money-mutation';

const FOCUS_REFETCH_THROTTLE_MS = 30_000;

export interface FinancialSummaryActivityItem {
  id: string;
  activity_type: string;
  description: string | null;
  entity_type: string | null;
  entity_id: string | null;
  amount: number | null;
  direction: 'in' | 'out' | null;
  created_at: string;
}

export interface FinancialSummary {
  period: { start: string; end: string; label: string };
  money_in: number;
  expenses: number;
  net: number;
  outstanding: {
    total: number;
    invoice_count: number;
    oldest_days: number;
    buckets: {
      current: { amount: number; count: number };
      '1_30': { amount: number; count: number };
      '31_60': { amount: number; count: number };
      '61_plus': { amount: number; count: number };
    };
  };
  available_balance_cents: number | null;
  in_transit_cents: number | null;
  recent_activity: FinancialSummaryActivityItem[];
  tax_summary: {
    quarter_label: string;
    tax_mode: 'charge' | 'exempt' | 'quick_method';
    hst_collected: number;
    itcs: number;
    quick_method_credit: number | null;
    quick_method_remittance_rate: number | null;
    net_owing: number;
    due_date: string | null;
    days_until_due: number | null;
    status: 'open' | 'filed' | 'amended' | 'reopened' | 'exempt';
  };
  t2125_progress: {
    ytd_expenses_count: number;
    categorized_count: number;
    uncategorized_count: number;
    completion_pct: number;
  };
  disputes_open: { count: number; amount: number };
  prior_money_in: number | null;
  prior_expenses: number | null;
  prior_net: number | null;
  prior_outstanding_total: number | null;
}

export interface UseFinancialSummaryReturn {
  data: FinancialSummary | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: MoneyError | null;
  requiresBusinessSelection: boolean;
  refetch: () => Promise<void>;
}

interface RawRpcEnvelope {
  success: boolean;
  period?: { start: string; end: string; label: string };
  money_in?: number | null;
  expenses?: { total: number; count: number; review_count: number } | null;
  net?: number | null;
  outstanding?: FinancialSummary['outstanding'];
  available_balance_cents?: number | null;
  in_transit_cents?: number | null;
  recent_activity?: Array<{
    id?: string;
    activity_type: string;
    description: string | null;
    entity_type: string | null;
    entity_id: string | null;
    amount: number | null;
    direction: 'in' | 'out' | null;
    created_at: string;
  }>;
  tax_summary?: {
    quarter_label?: string;
    current_period?: string;
    tax_mode?: 'charge' | 'exempt' | 'quick_method' | null;
    hst_collected: number;
    itcs?: number;
    quick_method_credit?: number | null;
    quick_method_remittance_rate?: number | null;
    net_owing?: number;
    due_date: string | null;
    days_until_due: number | null;
    status: string;
  };
  t2125_progress?: {
    ytd_expenses_count?: number;
    categorized_count?: number;
    uncategorized_count?: number;
    completion_pct?: number;
  } | null;
  disputes_open?: { count: number; amount: number };
  prior_money_in?: number | null;
  prior_expenses?: number | null;
  prior_net?: number | null;
  prior_outstanding_total?: number | null;
}

function normalizeEnvelope(raw: RawRpcEnvelope): FinancialSummary {
  const expenseTotal = raw.expenses?.total ?? 0;
  return {
    period: raw.period ?? { start: '', end: '', label: '' },
    money_in: raw.money_in ?? 0,
    expenses: expenseTotal,
    net: raw.net ?? 0,
    outstanding: raw.outstanding ?? {
      total: 0,
      invoice_count: 0,
      oldest_days: 0,
      buckets: {
        current: { amount: 0, count: 0 },
        '1_30': { amount: 0, count: 0 },
        '31_60': { amount: 0, count: 0 },
        '61_plus': { amount: 0, count: 0 },
      },
    },
    available_balance_cents: raw.available_balance_cents ?? null,
    in_transit_cents: raw.in_transit_cents ?? null,
    recent_activity: (raw.recent_activity ?? []).map((row, idx) => ({
      // activity_log.id is the natural unique key for feed rows (Migration 289).
      // Composite fallback is a guardrail only — should never fire post-289 since
      // the RPC always emits id. Using entity_id alone (the prior shape) collided
      // for business-scoped rows where entity_id == business_id.
      id: row.id ?? `${row.entity_type ?? 'none'}-${row.entity_id ?? 'none'}-${row.activity_type}-${row.created_at}-${idx}`,
      activity_type: row.activity_type,
      description: row.description,
      entity_type: row.entity_type,
      entity_id: row.entity_id,
      amount: row.amount ?? null,
      direction: row.direction,
      created_at: row.created_at,
    })),
    tax_summary: {
      quarter_label: raw.tax_summary?.quarter_label ?? raw.tax_summary?.current_period ?? '',
      tax_mode: (raw.tax_summary?.tax_mode ?? 'charge') as 'charge' | 'exempt' | 'quick_method',
      hst_collected: raw.tax_summary?.hst_collected ?? 0,
      itcs: raw.tax_summary?.itcs ?? 0,
      quick_method_credit: raw.tax_summary?.quick_method_credit ?? null,
      quick_method_remittance_rate: raw.tax_summary?.quick_method_remittance_rate ?? null,
      net_owing: raw.tax_summary?.net_owing ?? 0,
      due_date: raw.tax_summary?.due_date ?? null,
      days_until_due: raw.tax_summary?.days_until_due ?? null,
      status: (raw.tax_summary?.status ?? 'open') as 'open' | 'filed' | 'amended' | 'reopened' | 'exempt',
    },
    t2125_progress: {
      ytd_expenses_count: raw.t2125_progress?.ytd_expenses_count ?? 0,
      categorized_count: raw.t2125_progress?.categorized_count ?? 0,
      uncategorized_count: raw.t2125_progress?.uncategorized_count ?? 0,
      completion_pct: raw.t2125_progress?.completion_pct ?? 0,
    },
    disputes_open: raw.disputes_open ?? { count: 0, amount: 0 },
    prior_money_in: raw.prior_money_in ?? null,
    prior_expenses: raw.prior_expenses ?? null,
    prior_net: raw.prior_net ?? null,
    prior_outstanding_total: raw.prior_outstanding_total ?? null,
  };
}

interface UseFinancialSummaryArgs {
  period: FinancialSummaryPeriod;
}

export function useFinancialSummary({ period }: UseFinancialSummaryArgs): UseFinancialSummaryReturn {
  const activeBusinessId = useActiveBusinessStore((s) => s.activeBusinessId);
  const requiresSelection = useActiveBusinessStore((s) => s.requiresSelection);

  const [data, setData] = useState<FinancialSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<MoneyError | null>(null);

  const lastFocusFetchRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);
  // B-3c retry-loop fix: keep `data` accessible to doFetch via a ref so the
  // callback identity doesn't depend on the data state. Otherwise every
  // setData / setIsLoading toggle re-creates doFetch, which re-fires the
  // mount-fetch effect — a tight retry loop on RPC errors.
  const dataRef = useRef<FinancialSummary | null>(null);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const doFetch = useCallback(
    async (manual: boolean) => {
      if (!activeBusinessId) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      // First load = no data yet. Refresh = data already in hand.
      // inFlightRef above already prevents concurrent invocations, so we
      // don't need to read isLoading here.
      const isInitial = dataRef.current === null;
      if (isInitial) setIsLoading(true);
      else if (manual) setIsRefreshing(true);

      try {
        const result = await callMoneyRpc<RawRpcEnvelope>('financial_summary', {
          p_business_id: activeBusinessId,
          p_period: period,
        });

        if (!mountedRef.current) return;

        if (result.success) {
          setData(normalizeEnvelope(result.data));
          setError(null);
        } else {
          setError(result.error);
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
        inFlightRef.current = false;
      }
    },
    [activeBusinessId, period],
  );

  useEffect(() => {
    setData(null);
    setError(null);
  }, [activeBusinessId, period]);

  useEffect(() => {
    if (!activeBusinessId) return;
    void doFetch(false);
  }, [activeBusinessId, period, doFetch]);

  // Page Visibility focus refetch (admin's analogue to mobile's useFocusEffect).
  useEffect(() => {
    if (!activeBusinessId) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current >= FOCUS_REFETCH_THROTTLE_MS) {
        lastFocusFetchRef.current = now;
        void doFetch(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [activeBusinessId, doFetch]);

  // Cache invalidation on Money mutations + active-business changes.
  useEffect(() => {
    const onMutation = () => {
      void doFetch(true);
    };
    const onBusinessChange = () => {
      // active business store change triggers the dependency-change effect above.
    };
    window.addEventListener(MONEY_MUTATION_EVENT, onMutation);
    window.addEventListener(ACTIVE_BUSINESS_CHANGED_EVENT, onBusinessChange);
    return () => {
      window.removeEventListener(MONEY_MUTATION_EVENT, onMutation);
      window.removeEventListener(ACTIVE_BUSINESS_CHANGED_EVENT, onBusinessChange);
    };
  }, [doFetch]);

  const refetch = useCallback(async () => {
    await doFetch(true);
  }, [doFetch]);

  return useMemo(
    () => ({
      data,
      isLoading,
      isRefreshing,
      error,
      requiresBusinessSelection: requiresSelection,
      refetch,
    }),
    [data, isLoading, isRefreshing, error, requiresSelection, refetch],
  );
}
